import NotFound from "./component/NotFound";
import "./App.css";
import { useEffect, useRef } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Sidebar from "./component/pages/Sidebar";
import Login from "./component/pages/Login";
import Overview from "./component/pages/Overview";
import Devices from "./component/pages/Devices";
import DeviceType from "./component/pages/deviceTypes/DeviceType";
import DeviceTypeDashboard from "./component/pages/deviceTypes/DeviceTypeDashboard";
import Configuration from "./component/pages/Configuration";
import DeviceInventory from "./component/pages/DeviceInventory";
import OtaUpdates from "./component/pages/OtaUpdates";
import Audit from "./component/pages/Audit";
import Diagnostics from "./component/pages/Diagnostics";
import Connectivity from "./component/pages/Connectivity";
import HealthMonitor from "./component/pages/HealthMonitor";
import EditDeviceDetails from "./component/pages/deviceongoing/EditDeviceDetails";
import Model from "./component/pages/deviceongoing/Model";
import WSMSDeviceDashboard from "./component/pages/deviceongoing/WSMSDeviceDashboard";
import Forbidden from "./component/pages/Forbidden";
import Users from "./component/pages/admin/Users";
import RolesandPermissions from "./component/pages/admin/RolesandPermissions";
import FirmwarePackages from "./component/pages/ota/FirmwarePackages";
import FirmwarePackageDetails from "./component/pages/ota/FirmwarePackageDetails";
import FirmwareDeployments from "./component/pages/ota/FirmwareDeployments";
import CreateDeployment from "./component/pages/ota/CreateDeploymentDialog";
import DeploymentDetail from "./component/pages/ota/DeploymentDetail";
import Organizations from "./component/pages/admin/Organizations.js";
import ProtectedRoute from "./component/ProtectedRoute.js";
import AuthLoadingscreen from "./component/common/AuthLoadingScreen.js";
import ProfilePage from "./component/pages/ProfilePage.js";
import { useAuth } from "./auth/useAuth.js";
import OrganizationDetails from "./component/pages/admin/OrganizationDetails.js";
import AcceptInvite from "./component/pages/AcceptInvite.js";
import ReportsPage from "./component/pages/ReportsPage.js";
import PricingPage from "./component/pages/PricingPage.jsx";
import AssetsList from "./component/pages/inventoryAsset/Assets/AssetsLists.jsx";
import AssetDetail from "./component/pages/inventoryAsset/Assets/AssetDetail.jsx";
import TransfersList from "./component/pages/inventoryAsset/Transfers/TransfersList.jsx";
import BindingsList from "./component/pages/inventoryAsset/bindings/BindingsList.jsx";
import MovementsList from "./component/pages/inventoryAsset/movements/MovementsList.jsx";
import LeasesList from "./component/pages/inventoryAsset/leases/LeasesList.jsx";
import LocationsList from "./component/pages/inventoryAsset/LocationsList.js";
import RMAList from "./component/pages/inventoryAsset/rma/RMAList.jsx";
import InventoryOverview from "./component/pages/inventoryAsset/overview/InventoryOverview.jsx";
import ManufacturingDashboard from "./component/pages/manufacturing/ManufacturingDashboard.jsx";
import ManufacturingPartners from "./component/pages/manufacturing/ManufacturingPartners.jsx";
import ManufacturingProducts from "./component/pages/manufacturing/ManufacturingProducts.jsx";
import ManufacturingOrders from "./component/pages/manufacturing/ManufacturingOrders.jsx";
import ManufacturingOrderDetail from "./component/pages/manufacturing/ManufacturingOrderDetail.jsx";
import ManufacturingUnitDetail from "./component/pages/manufacturing/ManufacturingUnitDetail.jsx";
import ProjectsDashboard from "./component/pages/projects/ProjectsDashboard.jsx";
import ProjectsCustomers from "./component/pages/projects/ProjectsCustomers.jsx";
import ProjectsDeployments from "./component/pages/projects/ProjectsDeployments.jsx";
import ProjectDetailDashboard from "./component/pages/projects/ProjectDetailDashboard.jsx";
import ProjectsSites from "./component/pages/projects/ProjectsSites.jsx";
import ProjectsSurveys from "./component/pages/projects/ProjectsSurveys.jsx";
import WebhooksPage from "./component/pages/integrations/WebhooksPage.jsx";
import CommonServiceLayer from "./component/pages/onem2m/CommonServiceLayer.jsx";
import { SidebarProvider, useSidebar } from "./auth/SidebarContext.js";
import DeploymentProgress from "./component/pages/ota/DeploymentProgress.js";
import Attendance from "./component/pages/admin/Attendance.js";

const SIDEBAR_EXPANDED_WIDTH = 290;
const SIDEBAR_COLLAPSED_WIDTH = 72;

function AppLayout() {
  const { isAuthenticated, authLoading, user } = useAuth();
  const { collapsed } = useSidebar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const contentScrollRef = useRef(null);
  const isSuperAdmin = user?.global_role === "superadmin";

  const sidebarWidth = collapsed
    ? SIDEBAR_COLLAPSED_WIDTH
    : SIDEBAR_EXPANDED_WIDTH;

  useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.search, location.hash]);

  if (authLoading) {
    return <AuthLoadingscreen />;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/overview" /> : <Login />}
      />
      <Route
        path="/accept-invite"
        element={
          isAuthenticated ? <AcceptInvite /> : <Navigate to="/login" replace />
        }
      />

      <Route
        path="/*"
        element={
          isAuthenticated ? (
            <Box
              display="flex"
              minHeight="100dvh"
              sx={{
                width: "100%",
                backgroundColor: "#EEF3F7",
              }}
            >
              <Sidebar />

              <Box
                ref={contentScrollRef}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  ml: isMobile ? 0 : `${sidebarWidth}px`,
                  width: isMobile ? "100%" : `calc(100% - ${sidebarWidth}px)`,
                  transition:
                    "margin-left 0.25s cubic-bezier(0.4,0,0.2,1), width 0.25s cubic-bezier(0.4,0,0.2,1)",
                  px: { xs: 1, sm: 1.5, md: 3 },
                  pb: { xs: 1.5, md: 3 },
                  pt: { xs: 8.5, md: 3 },
                  overflowY: "auto",
                  overflowX: "hidden",
                  boxSizing: "border-box",
                }}
              >
                <Routes>
                  <Route path="/" element={<Navigate to="/overview" />} />
                  <Route path="/overview" element={<Overview />} />

                  <Route
                    path="/device_onboarding"
                    element={
                      <ProtectedRoute permission="device.view">
                        <Devices />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/administration/organizations/:orgId"
                    element={
                      <ProtectedRoute permission="organization.view">
                        <OrganizationDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/device_onboarding/devicetypes"
                    element={
                      <ProtectedRoute permission="system.manage">
                        <DeviceType />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/device_onboarding/devicetypes/:deviceTypeId/dashboard"
                    element={
                      <ProtectedRoute permission="device.view">
                        <DeviceTypeDashboard />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="/configuration" element={<Configuration />} />
                  <Route
                    path="/device_inventory"
                    element={<DeviceInventory />}
                  />
                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute permission="reports.view">
                        <ReportsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pricing"
                    element={
                      <ProtectedRoute permission="organization.view">
                        <PricingPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/ota_update"
                    element={
                      <ProtectedRoute permission="firmware.read">
                        <OtaUpdates />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="/audit_logs" element={<Audit />} />
                  <Route path="/diagnostics" element={<Diagnostics />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/profile/security" element={<ProfilePage />} />

                  <Route
                    path="/devices/:deviceId/edit"
                    element={<EditDeviceDetails />}
                  />
                  <Route
                    path="/devices/:deviceId/wsms"
                    element={<WSMSDeviceDashboard />}
                  />
                  <Route path="/devices/:deviceId" element={<Model />} />
                  <Route path="/403" element={<Forbidden />} />

                  <Route
                    path="/health/connectivity"
                    element={<Connectivity />}
                  />
                  <Route
                    path="/health/health_monitor"
                    element={<HealthMonitor />}
                  />
                  <Route
                    path="/health/device_health"
                    element={<DeviceInventory />}
                  />

                  {/* ADMIN */}
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute permission="org.manage_users">
                        <Users />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/attendance"
                    element={
                      <ProtectedRoute permission="attendance.view">
                        <Attendance />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/organizations"
                    element={
                      <ProtectedRoute permission="organization.view">
                        <Organizations />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/roles"
                    element={
                      <ProtectedRoute permission="org.manage_roles">
                        <RolesandPermissions />
                      </ProtectedRoute>
                    }
                  />

                  {/* OTA */}
                  <Route
                    path="/ota/packages"
                    element={
                      <ProtectedRoute permission="system.manage">
                        <FirmwarePackages />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/ota/packages/:id"
                    element={
                      <ProtectedRoute permission="system.manage">
                        <FirmwarePackageDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/ota/deployments"
                    element={
                      <ProtectedRoute permission="system.manage">
                        <FirmwareDeployments />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/ota/deployments/create"
                    element={
                      <ProtectedRoute permission="system.manage">
                        <CreateDeployment />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/ota/deployments/:id/progress"
                    element={
                      <ProtectedRoute permission="system.manage">
                        <DeploymentProgress />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/ota/deployments/:id"
                    element={
                      <ProtectedRoute permission="system.manage">
                        <DeploymentDetail />
                      </ProtectedRoute>
                    }
                  />

                  {/* Inventory */}
                  <Route
                    path="/inventory/overview"
                    element={
                      <ProtectedRoute permission="inventory.asset.view">
                        <InventoryOverview />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inventory/assets"
                    element={
                      <ProtectedRoute permission="inventory.asset.view">
                        <AssetsList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inventory/assets/:id"
                    element={
                      <ProtectedRoute permission="inventory.asset.view">
                        <AssetDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inventory/transfers"
                    element={
                      <ProtectedRoute permission="inventory.transfer.view">
                        <TransfersList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inventory/bindings"
                    element={
                      <ProtectedRoute permission="inventory.binding.view">
                        <BindingsList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inventory/movements"
                    element={
                      <ProtectedRoute permission="inventory.movement.view">
                        <MovementsList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inventory/leases"
                    element={
                      <ProtectedRoute permission="inventory.lease.view">
                        <LeasesList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inventory/rma"
                    element={
                      <ProtectedRoute permission="inventory.rma.view">
                        <RMAList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inventory/locations"
                    element={
                      <ProtectedRoute permission="inventory.location.view">
                        <LocationsList />
                      </ProtectedRoute>
                    }
                  />

                  {/* Manufacturing */}
                  <Route
                    path="/manufacturing/overview"
                    element={
                      <ProtectedRoute permission="inventory.asset.view">
                        <ManufacturingDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/manufacturing/partners"
                    element={
                      <ProtectedRoute permission="inventory.asset.view">
                        <ManufacturingPartners />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/manufacturing/products"
                    element={
                      <ProtectedRoute permission="inventory.asset.view">
                        <ManufacturingProducts />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/manufacturing/orders"
                    element={
                      <ProtectedRoute permission="inventory.asset.view">
                        <ManufacturingOrders />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/manufacturing/orders/:id"
                    element={
                      <ProtectedRoute permission="inventory.asset.view">
                        <ManufacturingOrderDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/manufacturing/units/:id"
                    element={
                      <ProtectedRoute permission="inventory.asset.view">
                        <ManufacturingUnitDetail />
                      </ProtectedRoute>
                    }
                  />

                  {/* Projects */}
                  <Route
                    path="/projects/overview"
                    element={
                      <ProtectedRoute permission="project.view">
                        <ProjectsDashboard />
                      </ProtectedRoute>
                      //isSuperAdmin ? <ProjectsDashboard /> : <Navigate to="/overview" replace />
                    }
                  />
                  <Route
                    path="/projects/customers"
                    element={
                      <ProtectedRoute permission="customer.view">
                        <ProjectsCustomers />
                      </ProtectedRoute>
                      //isSuperAdmin ? <ProjectsCustomers /> : <Navigate to="/overview" replace />
                    }
                  />

                  <Route
                    path="/projects/deployments"
                    element={
                      <ProtectedRoute permission="project.view">
                        <ProjectsDeployments />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/projects/deployments/:projectId"
                    element={
                      <ProtectedRoute permission="project.view">
                        <ProjectDetailDashboard />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/projects/sites"
                    element={
                      <ProtectedRoute permission="site.view">
                        <ProjectsSites />
                      </ProtectedRoute>
                    }
                    //isSuperAdmin ? <ProjectsSites /> : <Navigate to="/overview" replace />
                    //}
                  />
                  <Route
                    path="/projects/surveys"
                    element={
                      <ProtectedRoute permission="survey.view">
                        <ProjectsSurveys />
                      </ProtectedRoute>
                    }
                    // isSuperAdmin ? <ProjectsSurveys /> : <Navigate to="/overview" replace />
                    // }
                  />

                  {/* Integrations */}
                  <Route
                    path="/integrations/webhooks"
                    element={
                      <ProtectedRoute permission="webhook.view">
                        <WebhooksPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Common Service Layer */}
                  <Route
                    path="/common-service/onem2m"
                    element={
                      <ProtectedRoute permission="device.view">
                        <CommonServiceLayer />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Box>
            </Box>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <SidebarProvider>
      <AppLayout />
    </SidebarProvider>
  );
}

export default App;
